import type { ClassValue } from 'clsx';

import { ImageMissingPlaceholder } from '@vs-shop/ui/imageMissingPlaceholder';
import { clsxm } from '@vs-shop/utils';

import {
  ProductCardImage as ProductCardImagePart,
  ProductCardImagePlaceholder,
  ProductCardVariant,
  type ProductCardVariant as ProductCardVariantName,
} from '@vs-shop/commerce-ui/productCard';

import {
  PRODUCT_CARD_IMAGE_BASE_HEIGHT,
  PRODUCT_CARD_IMAGE_BASE_WIDTH,
  PRODUCT_CARD_IMAGE_RECOMMENDATION_HEIGHT,
  PRODUCT_CARD_IMAGE_RECOMMENDATION_WIDTH,
} from '../../../../config/product';

import { resolveImageUrl } from '@vs-shop/engine/image/resolveImageUrl';

import { ImageFilter } from '@vs-shop/engine/image/ImageFilter';

type Props = {
  imagePath: string | null;
  alt: string;
  variant: ProductCardVariantName;
  className?: ClassValue;
};

/**
 * rcprofi's binding of the shared product-card image part.
 *
 * What the tenant owns stays here: which Liip crop each appearance asks for, and
 * the intrinsic size that goes with it. The part receives one final, renderable
 * URL and nothing else.
 *
 * A product with no imagery at all renders `ProductCardImagePlaceholder` — the
 * same box the photo would have occupied — rather than an `<img>` with nothing
 * to load. What happens when a URL that does exist fails to load belongs to the
 * image seam the part renders through, not to this file.
 */
export default function ProductCardImage({ imagePath, alt, variant, className }: Props) {
  const isVariantRecommendation = variant === ProductCardVariant.RECOMMENDATION;

  const filter = isVariantRecommendation
    ? ImageFilter.product_card_recommendation
    : ImageFilter.product_card;
  const width = isVariantRecommendation
    ? PRODUCT_CARD_IMAGE_RECOMMENDATION_WIDTH
    : PRODUCT_CARD_IMAGE_BASE_WIDTH;
  const height = isVariantRecommendation
    ? PRODUCT_CARD_IMAGE_RECOMMENDATION_HEIGHT
    : PRODUCT_CARD_IMAGE_BASE_HEIGHT;

  if (imagePath === null) {
    return (
      <ProductCardImagePlaceholder className={clsxm(className)}>
        <ImageMissingPlaceholder />
      </ProductCardImagePlaceholder>
    );
  }

  const src = resolveImageUrl(imagePath, filter);

  return (
    <ProductCardImagePart
      src={src}
      alt={alt}
      width={width}
      height={height}
      className={clsxm(className)}
    />
  );
}
